// RechnungDetailPage.tsx — Detailansicht einer Rechnung (/rechnungen/:id).
//
// Aufbau nach dem Access-Formular „Details Rechnung", weil die Sachbearbeitung
// genau diese Anordnung im Kopf hat: oben der Rechnungskopf mit Daten, Beträgen
// und Status, rechts daneben der Guthabenstand des KUNDEN, darunter die
// Positionen. Die Optik ist die verbindliche „.kvp-rd"-Sprache, nicht der
// Access-Look.
//
// Die Trennung der beiden Kopfblöcke ist fachlich, nicht dekorativ: links steht,
// was zu DIESER Rechnung gehört (und aus ihren Positionen gerechnet wird),
// rechts steht, was für den Kunden INSGESAMT gilt — Guthaben und vorgemerkte
// Zahlungen hängen am Kontakt, nicht am Beleg.
//
// Beträge und Status sind hier bewusst nicht editierbar: sie werden aus den
// Positionen abgeleitet (`recalcInvoice`/`deriveStatus` im Backend). Wer sie
// ändern will, ändert eine Position.

import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";

import RechnungPositionen, {
  type KopfPatch,
  type Position,
} from "../components/redesign/RechnungPositionen";
import { StatusBadge, statusText } from "../components/redesign/invoiceStatus";
import { formatEuro, formatDate } from "../components/redesign/contractRiskCells";
import { formatBetragEingabe as betragInput, parseBetrag } from "../components/redesign/geldEingabe";
import { serverMeldung } from "../components/redesign/serverMeldung";
import "../components/redesign/contactRedesign.css";
import { apiFetch } from "../utils/apiFetch";
import { useSmartBack } from "../hooks/smartBack";
import { useAuth } from "../context/AuthContext";

type Kontakt = {
  id: number;
  lastname?: string | null;
  firstName?: string | null;
  guthaben?: number | null;
  zahlungenVorgemerkt?: number | null;
  guthabenVorgemerkt?: number | null;
};

type Rechnung = {
  id: number;
  invoiceNbr: string;
  sentDate: string | null;
  invoiceDate: string | null;
  dueDate: string | null;
  status: number;
  manuallySettled: boolean;
  creditPaidOut: boolean;
  amountCustomer: number | null;
  amountCustomerOpen: number | null;
  amountInsurer: number | null;
  amountInsurerOpen: number | null;
  remark: string | null;
  contactId: number | null;
  contact?: Kontakt | null;
  paymentMethod?: { id: number; label?: string | null } | null;
  positions?: Position[];
};

/** ISO-Datum für `<input type="date">` — der Server liefert es bereits so. */
const dateInput = (wert: string | null | undefined): string => (wert ? String(wert).slice(0, 10) : "");


const RechnungDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const goBack = useSmartBack("/zahlungsverkehr");
  const { hasPermission } = useAuth();
  const bearbeitbar = hasPermission("invoices:update");
  // Eigenes Recht: `DELETE /invoice-positions/:id` verlangt `invoices:delete`.
  // Für `mitarbeiter` ist das auf TEAM-Ebene bewusst NICHT gesetzt — der Knopf
  // darf dann gar nicht erst erscheinen, statt in ein 403 zu laufen.
  const darfLoeschen = hasPermission("invoices:delete");

  const [rechnung, setRechnung] = useState<Rechnung | null>(null);
  const [positionen, setPositionen] = useState<Position[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Kopf-Formular (Versanddatum, Fälligkeit, Bemerkung, Guthabenstand) — mit
  // Speicherleiste, weil hier getippt wird. Die Schalter daneben speichern
  // dagegen sofort: ein einzelner Klick mit sofort sichtbarer Statuswirkung.
  const [versanddatum, setVersanddatum] = useState("");
  const [faelligkeit, setFaelligkeit] = useState("");
  const [bemerkung, setBemerkung] = useState("");
  const [guthaben, setGuthaben] = useState("");
  const [zahlungenVorgemerkt, setZahlungenVorgemerkt] = useState("");
  const [guthabenVorgemerkt, setGuthabenVorgemerkt] = useState("");

  const [speichert, setSpeichert] = useState(false);
  const [hinweis, setHinweis] = useState<string | null>(null);

  const uebernehmen = useCallback((r: Rechnung) => {
    setRechnung(r);
    if (r.positions) setPositionen(r.positions);
    setVersanddatum(dateInput(r.sentDate));
    setFaelligkeit(dateInput(r.dueDate));
    setBemerkung(r.remark ?? "");
    setGuthaben(betragInput(r.contact?.guthaben));
    setZahlungenVorgemerkt(betragInput(r.contact?.zahlungenVorgemerkt));
    setGuthabenVorgemerkt(betragInput(r.contact?.guthabenVorgemerkt));
  }, []);

  const laden = useCallback(() => {
    if (!id) return;
    setLoading(true);
    apiFetch<Rechnung>(`invoices/${id}`)
      .then(({ data }) => {
        if (data) uebernehmen(data);
        setError(data ? null : "Rechnung nicht gefunden");
      })
      .catch(() => setError("Rechnung konnte nicht geladen werden"))
      .finally(() => setLoading(false));
  }, [id, uebernehmen]);

  useEffect(laden, [laden]);

  /** Positionsänderung: Kopfsummen und Status kommen vom Server mit zurück. */
  const positionenGeaendert = useCallback(
    (aendere: (alt: Position[]) => Position[], kopf?: KopfPatch) => {
      setPositionen(aendere);
      if (kopf) setRechnung((alt) => (alt ? { ...alt, ...kopf } : alt));
    },
    [],
  );

  const dirty = useMemo(() => {
    if (!rechnung) return false;
    return (
      versanddatum !== dateInput(rechnung.sentDate) ||
      faelligkeit !== dateInput(rechnung.dueDate) ||
      bemerkung !== (rechnung.remark ?? "") ||
      guthaben !== betragInput(rechnung.contact?.guthaben) ||
      zahlungenVorgemerkt !== betragInput(rechnung.contact?.zahlungenVorgemerkt) ||
      guthabenVorgemerkt !== betragInput(rechnung.contact?.guthabenVorgemerkt)
    );
  }, [
    rechnung,
    versanddatum,
    faelligkeit,
    bemerkung,
    guthaben,
    zahlungenVorgemerkt,
    guthabenVorgemerkt,
  ]);

  const speichern = useCallback(async () => {
    if (!rechnung || speichert) return;
    setSpeichert(true);
    setHinweis(null);
    try {
      const kopfGeaendert =
        versanddatum !== dateInput(rechnung.sentDate) ||
        faelligkeit !== dateInput(rechnung.dueDate) ||
        bemerkung !== (rechnung.remark ?? "");

      if (kopfGeaendert) {
        const res = await apiFetch<Rechnung>(`invoices/${rechnung.id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sentDate: versanddatum || null,
            dueDate: faelligkeit || null,
            remark: bemerkung.trim() || null,
          }),
        });
        const neu = res.data;
        if (neu) {
          setRechnung((alt) => (alt ? { ...alt, ...neu, contact: alt.contact } : alt));
          // Auf die kanonisierte Serverform ziehen, sonst bliebe die
          // Speicherleiste stehen: der Server liefert z.B. "" als null zurück,
          // und der Dirty-Vergleich meldete weiter eine Abweichung.
          setVersanddatum(dateInput(neu.sentDate));
          setFaelligkeit(dateInput(neu.dueDate));
          setBemerkung(neu.remark ?? "");
        }
      }

      // Der Guthabenstand hängt am Kunden, nicht an der Rechnung — eigener Aufruf.
      const guthabenGeaendert =
        guthaben !== betragInput(rechnung.contact?.guthaben) ||
        zahlungenVorgemerkt !== betragInput(rechnung.contact?.zahlungenVorgemerkt) ||
        guthabenVorgemerkt !== betragInput(rechnung.contact?.guthabenVorgemerkt);

      if (guthabenGeaendert && rechnung.contactId) {
        const res = await apiFetch<{
          guthaben: number | null;
          zahlungenVorgemerkt: number | null;
          guthabenVorgemerkt: number | null;
        }>(`invoice-customer-credits/${rechnung.contactId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            guthaben: parseBetrag(guthaben),
            zahlungenVorgemerkt: parseBetrag(zahlungenVorgemerkt),
            guthabenVorgemerkt: parseBetrag(guthabenVorgemerkt),
          }),
        });
        const stand = res.data;
        if (stand) {
          setRechnung((alt) =>
            alt
              ? {
                  ...alt,
                  contact: { ...(alt.contact ?? { id: alt.contactId ?? 0 }), ...stand },
                }
              : alt,
          );
          setGuthaben(betragInput(stand.guthaben));
          setZahlungenVorgemerkt(betragInput(stand.zahlungenVorgemerkt));
          setGuthabenVorgemerkt(betragInput(stand.guthabenVorgemerkt));
        }
      }
    } catch (e) {
      setHinweis(serverMeldung(e, "Die Änderungen konnten nicht gespeichert werden."));
    } finally {
      setSpeichert(false);
    }
  }, [
    rechnung,
    speichert,
    versanddatum,
    faelligkeit,
    bemerkung,
    guthaben,
    zahlungenVorgemerkt,
    guthabenVorgemerkt,
  ]);

  /** Schalter am Kopf — ein Klick, sofort gespeichert. */
  const schalte = useCallback(
    async (patch: { manuallySettled?: boolean; creditPaidOut?: boolean }) => {
      if (!rechnung) return;
      setHinweis(null);
      try {
        const res = await apiFetch<Rechnung>(`invoices/${rechnung.id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(patch),
        });
        const neu = res.data;
        if (neu) setRechnung((alt) => (alt ? { ...alt, ...neu, contact: alt.contact } : alt));
      } catch (e) {
        setHinweis(serverMeldung(e, "Die Änderung war nicht möglich."));
      }
    },
    [rechnung],
  );

  const buchen = useCallback(async () => {
    if (!rechnung) return;
    setHinweis(null);
    try {
      // Erst sichern, was im Formular steht — `uebernehmen` unten setzt alle
      // Felder auf die Serverwerte zurück, und ungespeicherte Eingaben wären
      // sonst kommentarlos verloren.
      if (dirty) await speichern();

      const res = await apiFetch<Rechnung>(`invoices/${rechnung.id}/book`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sentDate: versanddatum || null }),
      });
      const neu = res.data;
      // `positions` bleibt undefined: die Buchung ändert die Positionen nicht,
      // und `uebernehmen` würde sie sonst mit einer leeren Liste überschreiben.
      if (neu) uebernehmen({ ...neu, positions: undefined, contact: rechnung.contact });
    } catch (e) {
      setHinweis(serverMeldung(e, "Die Rechnung konnte nicht gebucht werden."));
    }
  }, [rechnung, versanddatum, uebernehmen, dirty, speichern]);

  if (loading) {
    return (
      <div className="kvp-rd detail">
        <div className="dbody">Rechnung wird geladen…</div>
      </div>
    );
  }
  if (error || !rechnung) {
    return (
      <div className="kvp-rd detail">
        <div className="dbody rd-error">{error ?? "Rechnung nicht gefunden"}</div>
      </div>
    );
  }

  const kunde = [rechnung.contact?.lastname, rechnung.contact?.firstName]
    .filter(Boolean)
    .join(", ");
  const istEntwurf = rechnung.status === 0;

  return (
    <div className="kvp-rd detail">
      <div className="dhead">
        <button className="backlink" onClick={goBack}>
          ← Zurück zum Zahlungsverkehr
        </button>
        <div className="dtitle">
          <h1>Rechnung {rechnung.invoiceNbr}</h1>
          <StatusBadge status={rechnung.status} manuellErledigt={rechnung.manuallySettled} mitText />
        </div>
        <div className="metarow">
          {kunde && (
            <button
              className="lnk"
              onClick={() => rechnung.contactId && navigate(`/contacts/${rechnung.contactId}`)}
            >
              {kunde}
            </button>
          )}
          {rechnung.paymentMethod?.label && <span>{rechnung.paymentMethod.label}</span>}
          <span>{statusText(rechnung.status)}</span>
        </div>
      </div>

      <div className="dbody">
        {hinweis && <div className="rd-error">{hinweis}</div>}

        <div className="inv-kopf">
          {/* Links: die Rechnung selbst. */}
          <div className="inv-block">
            <h3>Rechnung</h3>
            <div className="inv-felder">
              <div className="inv-feld">
                <label htmlFor="inv-versand">Versanddatum</label>
                <input
                  id="inv-versand"
                  className="inp"
                  type="date"
                  value={versanddatum}
                  disabled={!bearbeitbar}
                  onChange={(e) => setVersanddatum(e.target.value)}
                />
              </div>
              <div className="inv-feld">
                <label htmlFor="inv-faellig">Fälligkeit</label>
                <input
                  id="inv-faellig"
                  className="inp"
                  type="date"
                  value={faelligkeit}
                  disabled={!bearbeitbar}
                  onChange={(e) => setFaelligkeit(e.target.value)}
                />
              </div>

              {/* Abgeleitet aus den Positionen — deshalb Anzeige, kein Feld. */}
              <div className="inv-feld">
                <label>Beitrag Kunde</label>
                <div className="inv-wert gross">{formatEuro(rechnung.amountCustomer)}</div>
              </div>
              <div className="inv-feld">
                <label>Beitrag Versicherer</label>
                <div className="inv-wert gross">{formatEuro(rechnung.amountInsurer)}</div>
              </div>
              <div className="inv-feld">
                <label>Beitrag Kunde offen</label>
                <div
                  className={`inv-wert${
                    (rechnung.amountCustomerOpen ?? 0) > 0 ? " txt-danger" : " leer"
                  }`}
                >
                  {formatEuro(rechnung.amountCustomerOpen)}
                </div>
              </div>
              <div className="inv-feld">
                <label>Beitrag Versicherer offen</label>
                <div
                  className={`inv-wert${
                    (rechnung.amountInsurerOpen ?? 0) > 0 ? " txt-warn" : " leer"
                  }`}
                >
                  {formatEuro(rechnung.amountInsurerOpen)}
                </div>
              </div>

              <div className="inv-feld">
                <label>Status</label>
                <StatusBadge
                  status={rechnung.status}
                  manuellErledigt={rechnung.manuallySettled}
                  mitText
                />
              </div>
              <div className="inv-feld">
                <label>Rechnungsdatum</label>
                <div className={`inv-wert${rechnung.invoiceDate ? "" : " leer"}`}>
                  {rechnung.invoiceDate ? formatDate(rechnung.invoiceDate) : "noch nicht gebucht"}
                </div>
              </div>
            </div>

            <div className="inv-schalter">
              <label>
                <input
                  type="checkbox"
                  checked={rechnung.creditPaidOut}
                  disabled={!bearbeitbar}
                  onChange={(e) => schalte({ creditPaidOut: e.target.checked })}
                />
                Guthaben ausgezahlt
              </label>
              <label title="Hakt die Rechnung ab, ohne die Positionsbeträge zu verändern — jederzeit rücknehmbar.">
                <input
                  type="checkbox"
                  checked={rechnung.manuallySettled}
                  disabled={!bearbeitbar}
                  onChange={(e) => schalte({ manuallySettled: e.target.checked })}
                />
                Manuell erledigt
              </label>

              {istEntwurf && bearbeitbar && (
                <button
                  className="inv-btn"
                  onClick={buchen}
                  disabled={positionen.length === 0}
                  title={
                    positionen.length === 0
                      ? "Erst eine Position anlegen"
                      : "Rechnungsdatum setzen und Fälligkeit aus dem Zahlungsziel rechnen"
                  }
                >
                  Rechnung buchen
                </button>
              )}
            </div>
          </div>

          {/* Rechts: der Kunde insgesamt — nicht dieser Beleg. */}
          <div className="inv-block guthaben">
            <h3>Kunde gesamt</h3>
            <div className="inv-felder eins">
              <div className="inv-feld">
                <label htmlFor="inv-guthaben">Guthaben</label>
                <input
                  id="inv-guthaben"
                  className="inp geld"
                  inputMode="decimal"
                  value={guthaben}
                  disabled={!bearbeitbar || !rechnung.contactId}
                  onChange={(e) => setGuthaben(e.target.value)}
                />
              </div>
              <div className="inv-feld">
                <label htmlFor="inv-zahlungen-vorgemerkt">Zahlungen vorgemerkt</label>
                <input
                  id="inv-zahlungen-vorgemerkt"
                  className="inp geld"
                  inputMode="decimal"
                  value={zahlungenVorgemerkt}
                  disabled={!bearbeitbar || !rechnung.contactId}
                  onChange={(e) => setZahlungenVorgemerkt(e.target.value)}
                />
              </div>
              <div className="inv-feld">
                <label htmlFor="inv-guthaben-vorgemerkt">Guthaben vorgemerkt</label>
                <input
                  id="inv-guthaben-vorgemerkt"
                  className="inp geld"
                  inputMode="decimal"
                  value={guthabenVorgemerkt}
                  disabled={!bearbeitbar || !rechnung.contactId}
                  onChange={(e) => setGuthabenVorgemerkt(e.target.value)}
                />
              </div>
              <div className="inv-feld">
                <label htmlFor="inv-bemerkung">Bemerkung</label>
                <input
                  id="inv-bemerkung"
                  className="inp"
                  value={bemerkung}
                  disabled={!bearbeitbar}
                  onChange={(e) => setBemerkung(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <RechnungPositionen
          invoiceId={rechnung.id}
          contactId={rechnung.contactId}
          positionen={positionen}
          bearbeitbar={bearbeitbar}
          darfLoeschen={darfLoeschen}
          onGeaendert={positionenGeaendert}
        />
      </div>

      {bearbeitbar && dirty && (
        <div className="cd-savebar">
          <div className="cd-savebar-left">
            <span className="cd-savebar-note">Ungespeicherte Änderungen</span>
          </div>
          <button className="cd-save" onClick={speichern} disabled={speichert}>
            {speichert ? "Wird gespeichert …" : "Speichern"}
          </button>
        </div>
      )}
    </div>
  );
};

export default RechnungDetailPage;
